import { useState, useCallback, useRef } from 'react';
import {
  Stack,
  Button,
  Inline,
  InlineItem,
  LabelText,
  SegmentedControl,
  SegmentedControlItem,
  Text,
  Icon,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import {
  StyledTextarea,
  ProductPickerList,
  ProductPickerItem,
  ProductPickerLabel,
  ProductPickerName,
  ProductPickerDescription,
  ProductPickerCheck,
  FileInputLabel,
  FileList,
  FileListItem,
  FileName,
  FileSize,
  FileRemoveButton,
} from './feedback-form.styles';

const PRODUCTS = [
  {
    value: 'nzo',
    label: 'Kinesis for Organisations',
    description: 'Organisation-level sustainability reporting and benchmarking',
  },
  {
    value: 'community-decarbonisation',
    label: 'Kinesis for Communities',
    description: 'Community-scale decarbonisation planning',
  },
  {
    value: 'workspaces',
    label: 'Kinesis for Precincts',
    description: 'Precinct and building performance modelling',
  },
  {
    value: 'platform',
    label: 'Kinesis Platform, Authentication, API and Settings',
    description: 'Platform infrastructure, authentication, and configuration',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Something else not listed above',
  },
];

const CATEGORIES = [
  { value: 'feature-request', label: 'Feature Request' },
  { value: 'bug', label: 'Bug' },
  { value: 'general-feedback', label: 'General Feedback' },
];

const CATEGORY_HINTS: Record<string, string> = {
  'feature-request':
    'Focus on what you are trying to achieve, rather than how to build it.',
  bug: 'Be clear and specific. Links to the affected page or feature are helpful.',
  'general-feedback':
    "Tell us what's on your mind \u2014 what's working well, what's frustrating, or what could be better.",
};

const ACCEPTED_FILE_TYPES = '.png,.jpg,.jpeg';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILE_COUNT = 3;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FeedbackForm = (props: FeedbackFormProps) => {
  const { defaultProduct, onSubmit = noop, isSubmitting = false } = props;

  const [product, setProduct] = useState<string>(defaultProduct ?? '');
  const [category, setCategory] = useState<string>('feature-request');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedProduct = PRODUCTS.find((p) => p.value === product);

  const canSubmit =
    product !== '' &&
    category !== '' &&
    description.trim() !== '' &&
    !isSubmitting;

  const handleFilesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFileError(null);
      const selected = Array.from(e.target.files ?? []);
      const combined = [...files, ...selected];

      if (combined.length > MAX_FILE_COUNT) {
        setFileError(`You can attach up to ${MAX_FILE_COUNT} files.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const oversized = combined.find((f) => f.size > MAX_FILE_SIZE);
      if (oversized) {
        setFileError(`"${oversized.name}" exceeds the 5 MB limit.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setFiles(combined);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [files],
  );

  const handleFileRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({
      product,
      productLabel: selectedProduct?.label ?? product,
      category,
      description: description.trim(),
      attachments: files.length > 0 ? files : undefined,
    });
  }, [
    canSubmit,
    product,
    selectedProduct,
    category,
    description,
    files,
    onSubmit,
  ]);

  return (
    <Stack space='large'>
      <Stack space='medium'>
        <Stack space='small'>
          <LabelText>Product</LabelText>
          <ProductPickerList>
            {PRODUCTS.map((p) => (
              <ProductPickerItem
                key={p.value}
                type='button'
                $selected={product === p.value}
                onClick={() => setProduct(p.value)}
              >
                <ProductPickerLabel>
                  <ProductPickerName>{p.label}</ProductPickerName>
                  <ProductPickerDescription>
                    {p.description}
                  </ProductPickerDescription>
                </ProductPickerLabel>
                <ProductPickerCheck>
                  {product === p.value && (
                    <Icon type='check-circle' magnitude='small' />
                  )}
                </ProductPickerCheck>
              </ProductPickerItem>
            ))}
          </ProductPickerList>
        </Stack>
        <Stack space='small'>
          <LabelText>Category</LabelText>
          <SegmentedControl
            value={category}
            onChange={(value: string | number) => setCategory(String(value))}
            widthMode='fill-container'
          >
            {CATEGORIES.map((c) => (
              <SegmentedControlItem key={c.value} value={c.value}>
                {c.label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Stack>
        <Stack space='small'>
          <LabelText>Description</LabelText>
          {CATEGORY_HINTS[category] && (
            <Text tone='secondary'>{CATEGORY_HINTS[category]}</Text>
          )}
          <StyledTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Describe your feedback...'
          />
        </Stack>
        <Stack space='small'>
          <LabelText>Attachments (optional)</LabelText>
          <Text tone='secondary'>
            Screenshots or files to help illustrate your feedback.
          </Text>
          <FileInputLabel>
            <Icon type='upload' magnitude='small' />
            Choose files
            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPTED_FILE_TYPES}
              multiple
              onChange={handleFilesChange}
              style={{ display: 'none' }}
            />
          </FileInputLabel>
          {fileError && <Text tone='critical'>{fileError}</Text>}
          {files.length > 0 && (
            <FileList>
              {files.map((file, index) => (
                <FileListItem key={`${file.name}-${index}`}>
                  <FileName>{file.name}</FileName>
                  <FileSize>{formatFileSize(file.size)}</FileSize>
                  <FileRemoveButton
                    type='button'
                    onClick={() => handleFileRemove(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <Icon type='close' magnitude='small' />
                  </FileRemoveButton>
                </FileListItem>
              ))}
            </FileList>
          )}
        </Stack>
      </Stack>
      <Inline>
        <InlineItem sizing='fill-container' />
        <InlineItem sizing='fit-content'>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit feedback'}
          </Button>
        </InlineItem>
      </Inline>
    </Stack>
  );
};

export type FeedbackFormProps = {
  defaultProduct?: string;
  onSubmit?: (value: {
    product: string;
    productLabel: string;
    category: string;
    description: string;
    attachments?: File[];
  }) => void;
  isSubmitting?: boolean;
};

export { FeedbackForm, PRODUCTS };
